import { z } from 'zod';

/**
 * Canonical filter schemas, shared by client and server.
 *
 * These live in `@shared` (not the client) so the server can validate the same
 * `Filter` shape when persisting saved views. The client re-exports them from
 * `client/src/components/filters/core/schema.ts` to keep existing import paths.
 */
export const FilterOperatorSchema = z.enum([
    'equals',
    'not',
    'in',
    'notIn',
    'contains',
    'startsWith',
    'endsWith',
    'gt',
    'gte',
    'lt',
    'lte',
    'isNull',
    'isNotNull',
    'between',
    'notBetween',
    // Relative date ranges (Today, Last 30 days, This month, …). Resolved to a gte/lte range from
    // a stored token at where-build time, so a saved view stays relative to "now".
    'withinRelative',
]);

/**
 * Mirrors the `FilterValue` union in the client filter core types so
 * URL-deserialised filters can be typed without falling back to `any`.
 *
 * Note: this in-memory schema admits `bigint`/`Date`. Stored (JSONB) saved-view
 * payloads must NOT — see `StoredSavedViewPayloadSchema` in `./saved-view`.
 */
export const FilterValueSchema = z.union([
    z.string(),
    z.number(),
    z.coerce.bigint(),
    z.boolean(),
    z.date(),
    z.null(),
    z.array(z.string()),
    z.array(z.number()),
    z.array(z.coerce.bigint()),
    z.array(z.date()),
    z.array(z.coerce.bigint().nullable()),
]);

/**
 * Base Zod schema for the `Filter` interface.
 */
export const FilterSchema = z.object({
    id: z.string(),
    filterId: z.string(),
    operator: FilterOperatorSchema,
    value: FilterValueSchema,
});

export type FilterSchemaType = z.infer<typeof FilterSchema>;
